declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export const GTM_ID = "GTM-W4N5FDC";

/** Where Google's ads wait for the visitor's consent: the EEA, the UK and Switzerland, as marketing listed them. */
const CONSENT_REGIONS = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO",
  "GB",
  "CH",
];

/** Everywhere else, everything is granted from the start. */
const DEFAULT_CONSENT = {
  analytics_storage: "granted",
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
};

/** In those regions, analytics is granted and ads wait up to half a second for the banner's answer. */
const REGIONAL_CONSENT = {
  analytics_storage: "granted",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  region: CONSENT_REGIONS,
  wait_for_update: 500,
};

/**
 * Google's consent defaults (Consent Mode v2), which have to be in the data
 * layer before CookieYes hands Google the visitor's choice (`consent update`)
 * and before GTM starts, so a marketing document runs them first in its head.
 * They are rendered with the head, not left to the Studio's custom code: the
 * server prints that after CookieYes, and React, which hydrates the whole
 * document, takes an inline script it did not render for its own next one and
 * overwrites it with that one's text, the page's structured data included.
 */
export const CONSENT_DEFAULTS = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag("consent","default",${JSON.stringify(DEFAULT_CONSENT)});gtag("consent","default",${JSON.stringify(REGIONAL_CONSENT)});`;

/**
 * GTM's start, the first half of the snippet the container recommends:
 * the data layer, and GTM's start event in it. A marketing document runs it
 * in its head straight after CookieYes, where the published site's head runs
 * the snippet, because CookieYes hands the visitor's consent to Google
 * (`set developer_id`, `consent update`, `cookie_consent_update`) only into a
 * data layer that already exists when its banner script starts, and it looks
 * once. Without it Google sees no consent at all and serves European visitors
 * non-personalised ads: on 25 September 2026 the published site sent Ads
 * account AW-831932776 its remarketing ping and this site did not.
 */
export const GTM_START = `window.dataLayer=window.dataLayer||[];window.dataLayer.push({"gtm.start":new Date().getTime(),event:"gtm.js"});`;

/** Whether the document's head has already started GTM (`GTM_START`). */
function gtmStarted(): boolean {
  return (window.dataLayer ?? []).some((entry) => entry?.event === "gtm.js");
}

/**
 * The GTM bootstrap, run from the root element's ref callback instead of the
 * parse-time inline snippet the container recommends. The app hydrates the
 * whole document (`hydrateRoot(document)` in `src/client.tsx`), so everything
 * GTM and the tags it loads inject before hydration finishes, scripts,
 * origin-trial `<meta>`s, tracking-pixel elements, lands inside React's
 * hydration diff and intermittently trips minified error #418 on production
 * loads with warm tag caches (r-1903). Ref callbacks fire in the commit
 * phase, after the hydration diff of the tree has completed (and never on the
 * server), so every tag-driven mutation happens on a document React has
 * finished hydrating. Nothing is lost: `dataLayer` pushes made before GTM
 * loads queue in the array seeded here, and GTM drains the queue when it
 * boots. The start event is pushed here unless the head already did
 * (`GTM_START`), so GTM starts once either way.
 */
export function bootGtm(node: HTMLHtmlElement | null) {
  if (!node || document.getElementById("gtag-base")) {
    return;
  }
  if (!gtmStarted()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
  }
  const script = document.createElement("script");
  script.id = "gtag-base";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}
